import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button } from '@mui/material';
import FormGenerator from '@sharedCR/common/formGenerator';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { noneOption, suffixOptions } from 'constants/options';
import useForm from 'hooks/useForm';
import { normalizeRequestPhoneNumber } from 'utils/data';
import { getChangedFieldsByConfig, getConfigInfo } from 'utils/loanManagement';
import { capitalizeFirstLetterInNameField } from 'utils/string';

import { closeDialogAction, showMessageAction } from 'store/global/actions';
import {
  createLoanTitleHolderAction,
  editLoanTitleHolderAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
} from 'store/loanApplications/actions';
import { selectEntityChangedPropsById } from 'store/loanApplications/selectors';

import { LoanClosingTestIds } from '../LoanClosing.testIds';
import { titleHolderEditConfig } from '../TitleHolderInfo/TitleHolderInfoEdit.config';

const TitleHolderInfoPersonForm = ({
  formRef,
  titleHolder,
  isLoading,
  setIsLoading,
  applicationId,
}) => {
  const dispatch = useDispatch();

  const titleHolderId = titleHolder?.titleHolderId;

  const pendingChangesData = useSelector(selectEntityChangedPropsById(titleHolderId));

  const { initialFormValues, initialConfig, schema, fieldsConfig } = useMemo(
    () =>
      getConfigInfo({
        applicationData: titleHolder,
        config: titleHolderEditConfig,
      }),
    [titleHolder],
  );

  const extendedInitialFormValues = useMemo(
    () => ({
      ...initialFormValues,
      suffix: suffixOptions.find(({ value }) => value === titleHolder?.suffix) || noneOption,
    }),
    [initialFormValues, titleHolder?.suffix],
  );

  const {
    form,
    handleChange,
    validationErrors,
    setSchema,
    setInForm,
    isFormChanged,
    validateField,
    validateForm,
  } = useForm(extendedInitialFormValues, fieldsConfig);

  const { changedFields } = useMemo(
    () => getChangedFieldsByConfig({ initialConfig, pendingChangesData }),
    [initialConfig, pendingChangesData],
  );

  useEffect(() => {
    setSchema(schema);
  }, [setSchema, schema]);

  const submitForm = useCallback(async () => {
    if (!(await validateForm())) {
      return;
    }

    setIsLoading(true);

    const valuesToSend = {
      ...form,
      applicationId,
      firstName: capitalizeFirstLetterInNameField(form.firstName),
      middleName: capitalizeFirstLetterInNameField(form.middleName || null),
      lastName: capitalizeFirstLetterInNameField(form.lastName),
      suffix: form.suffix?.value,
      cellPhoneNumber: normalizeRequestPhoneNumber(form.cellPhoneNumber),
      titleHolderId,
      email: form.email || null,
      requestMetadata: { tabId: LoanApplicationTabId.PROPERTY },
    };

    const successMessage = `Title Holder has been ${
      titleHolderId ? 'edited' : 'created'
    } and is ready to be stored in the loan record`;

    const titleHolderAction = titleHolderId
      ? editLoanTitleHolderAction
      : createLoanTitleHolderAction;

    dispatch(titleHolderAction(valuesToSend))
      .then(() => {
        batch(() => {
          dispatch(showMessageAction({ message: successMessage, variant: 'success' }));
          dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
          dispatch(getLoanApplicationHeaderViewAction({ applicationId }));

          dispatch(closeDialogAction());
        });
      })
      .catch(({ response }) =>
        handleLoanFeesError({ response, dispatch, onRetryAction: submitForm }),
      )
      .finally(() => setIsLoading(false));
  }, [validateForm, setIsLoading, form, applicationId, titleHolderId, dispatch]);

  const onSubmitTitleHolder = useCallback(
    e => {
      e.preventDefault();

      submitForm();
    },
    [submitForm],
  );

  return (
    <form
      noValidate
      className="flex flex-col justify-center p-24 w-400"
      data-ischanged={isFormChanged}
      ref={formRef}
      onSubmit={onSubmitTitleHolder}
    >
      <FormGenerator
        changedFields={changedFields}
        config={titleHolderEditConfig}
        form={form}
        formName={FormNames.TITLE_HOLDER_INFORMATION}
        handleChange={handleChange}
        isLoading={isLoading}
        numberOfColumns={1}
        setInForm={setInForm}
        validateField={validateField}
        validationErrors={validationErrors}
      />

      <div className="flex justify-end">
        <Button
          className="whitespace-nowrap"
          data-testid={LoanClosingTestIds.TITLE_HOLDER_INFO_PERSON_FORM_SUBMIT_BUTTON}
          disabled={isLoading || !isFormChanged}
          type="submit"
          variant="contained"
        >
          Save
        </Button>
      </div>
    </form>
  );
};

TitleHolderInfoPersonForm.propTypes = {
  ...WithLoadingPropTypes,
  formRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  applicationId: PropTypes.string.isRequired,
  titleHolder: PropTypes.object,
};

export default compose(withLoading, memo)(TitleHolderInfoPersonForm);
